import 'view/css/payment.css';

import moment from 'moment';
import React, { useState, useEffect } from 'react';

import DatePicker from 'react-datepicker';

import PopMessage from 'component/pop-msg/PopMessage';
import PrintAdjustPanel from 'view/print/PrintAdjustPanel';
import PrintPreview from 'view/print/PrintPreview';

import API from 'service/api';
import Util, { Data, padZero, rearrange2d } from 'service/util';
import Loading from 'component/Loading';
import ModalScreen from 'component/ModalScreen';
import EReceiptModal from 'component/e-invoice/EReceiptModal';
import Protected from 'component/Protected';

class PaymentDTO
{
  constructor(payment)
  {
    Object.assign(this, payment);

    this.create_dtm = Util.ShortDate(this.create_dtm);
    this.transfer_dtm = Util.ShortDate(this.transfer_dtm);
    this.amount = `$${this.amount}`;

    if (!this.pay_month) this.pay_month = [];

    let [lic, loc, psn] = Object.values(this.pay_month).reduce((r,item) => {
      let { l, p, dis, mgt, loc } = item;
      r[0].add(l);
      r[1].add(dis ? { dis, mgt, loc } : null);
      r[2].add(p);
      return r;
    }, [new Set(), new Set(), new Set()]);

    this.arr_loc = Array.from(loc).sort(Util.sortLocationObject);
    this.arr_psn = Array.from(psn);
    this.arr_lic = Array.from(lic).sort();
  }
  get key() { return `${this.company}-${this.ix_yrmo}-${this.uid}-${this.useq}`; }
  get id() { return `${this.company}IV-${this.ix_yrmo}-${padZero(this.uid, 7)}-${padZero(this.useq, 2)}`; }

  get loc_key() {
    return Array.from(new Set(this.arr_loc.map(x => {
      if (x) return `${x.dis}${x.loc} (${x.mgt})`;
      return '';
    }))).join('\n');
  }
  get loc_psn() {
    return this.arr_psn.join('\n');
  }
  get license() {
    return this.arr_lic.reduce((r,x) =>{
      let a = r[r.length - 1];
      if (a.length >= 6) r.push(a = []);
      a.push(x);
      return r;
    }, [[]]).map(a => a.join(', ')).join('\n') || '*';
  }
  get first_license() {
    return this.arr_lic?.[0] || ''
  }
  get pay_for() {
    if (0 == this.pay_month.length) return '';
    const S = (v) => { v = String(v); return v.substr(0, 4) + '-' + v.substr(4, 2); }
    if (this.pay_month.length < 2) {
      let p = this.pay_month[0];
      return p.n == p.x ? S(p.n) : `${S(p.n)}至${S(p.x)}`;
    }
    return this.pay_month.map(p =>
      p.n == p.x ? `${p.l}: ${S(p.n)}` : `${p.l}: ${S(p.n)}至${S(p.x)}`
    ).join('\n');
  }

}

//===================================================

const PayReceiptView = () =>
{
  const [modal,      setModal     ] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);

  const [from_dtm, setFromDate] = useState(new Date());
  const [to_dtm,   setToDate  ] = useState(new Date());

  const [print_addOn, setAddOn] = useState({ msg: '', x: 140, y: 53, fontSize: 5 });

  const [by_date, setByDate]    = useState('trans');
  const [company, setCompany]   = useState(Data.company[0].cd);
  const [license, setLicense]   = useState('');

  const [config,   setConfig]   = useState(null);
  const [dict,     setDict]     = useState(null);
  const [payments, setPayments] = useState(null);

  //-----------------------------------------
  // functions

  const refreshFilter = () =>
  {
    if (!dict || !company) return;

    let ls = dict?.[company];
    if (ls)
    { // has data, then filter
      if (license) ls = ls.filter(x => -1<x.license.indexOf(license));
    }

    setPayments(ls);
  }

  const showEReceiptModal = () => {
    setModal(<EReceiptModal payments={dict} addOns={print_addOn} />);
    setModalTitle('電子收據');
  }

  const closeModal = () => {
    setModal(null);
    setModalTitle(null);
  }

  //-----------------------------------------
  // init
  useEffect(() => {
    if ( moment(from_dtm).isAfter(to_dtm) )
    {
      let [t, f] = [from_dtm, to_dtm];
      setFromDate(f);
      setToDate(t);
      return;
    }

    if ( 31 < Math.abs(moment(to_dtm).diff(from_dtm, 'days')) )
    {
      PopMessage.show('必需選擇三十日以內。', '警告', 'danger');
      return;
    }

    setDict(null);
    setPayments(null);
    API.getPaymentByDate(by_date, from_dtm, to_dtm).then(data => {
      const today = moment().format('DD/MM/YYYY');

      let dict = data.filter(pay =>
        !pay.is_skipreceipt &&
        -1<['C','T','Q'].indexOf(pay.payment_type)
      ).reduce((r, pay) => {
        // process
        pay.pay_month = (pay.pay_month||null) && JSON.parse(pay.pay_month);
        // setup dictionary
        if (!(pay.company in r)) r[pay.company] = [];
        // push
        let o = new PaymentDTO(pay);
        o.today = today;
        r[pay.company].push(o);
        return r;
      }, {});

      Object.keys(dict).forEach(com => {
        let ori = dict[com].sort((x,y) => {
          let a = x.arr_loc[0];
          let b = y.arr_loc[0];
          if (a == null && b == null) return 0;
          if (a == null) return -1;
          if (b == null) return  1;
          if (a.dis > b.dis) return  1;
          if (a.dis < b.dis) return -1;
          if (a.mgt > b.mgt) return  1;
          if (a.mgt < b.mgt) return -1;
          if (a.loc > b.loc) return  1;
          if (a.loc < b.loc) return -1;
          return 0;
        });

        let nwa = rearrange2d(ori);
        dict[com] = nwa;

      });
      setDict(dict);

    });
  }, [by_date, from_dtm, to_dtm]);

  useEffect(() => {
    let config = Cache.Config.print[`${company}@receipt`];
    setConfig(config);
    refreshFilter();
  }, [dict, company]);

  useEffect(() => {
    refreshFilter();
  }, [license]);


  //-----------------------------------------
  // render
  return (
    <>
      <h1>日常操作 - 收據</h1>
      <div className="action-panel">
        <div className="row search-bar">
          <label className="field-row">
            <strong>公司</strong>
            <select value={company} onChange={e=>setCompany(e.target.value)}>
              { Data.company.map(com =>
                <option key={com.cd} value={com.cd}>{com.cd} - {com.name}</option>
              )}
            </select>
          </label>
          <label className="field-row">
            <select value={by_date} onChange={e=>setByDate(e.target.value)}>
              <option value="trans">入賬</option>
              <option value="create">建立</option>
              <option value="modify">修改</option>
            </select>
          </label>
          <label className="field-row">
            <strong>日期由</strong>
            <div className="date-field">
              <DatePicker selected={from_dtm} dateFormat="yyyy-MM-dd" onChange={setFromDate} required />
            </div>
          </label>
          <label className="field-row">
            <strong>至</strong>
            <div className="date-field">
              <DatePicker selected={to_dtm} dateFormat="yyyy-MM-dd" onChange={setToDate} required />
            </div>
          </label>
          { !!dict &&
            <label className="field-row">
              <strong>車牌</strong>
              <input value={license} onChange={e=>setLicense(e.target.value.trim().toUpperCase())} />
            </label>
          }
        </div>
        <PrintAdjustPanel onChange={setAddOn} />
      </div>
      <div className="btn-group">
        <Protected allow={['ADMIN','OPERATOR']}>
          <button className="btn btn-small blue" onClick={showEReceiptModal}>電子收據</button>
        </Protected>
      </div>
      <div>
        { !dict ? <Loading/>
          :(!payments || payments.length <= 0) ? <p className="message yellow">沒有記錄</p>
          :<div className="overdue">
            <PrintPreview items={payments} company={company} printId="receipt" config={config} addOn={print_addOn} />
          </div>
        }
      </div>
      <ModalScreen show={!!modal}
        title={modalTitle}
        onClose={closeModal}>
        { modal }
      </ModalScreen>
    </>
  );

}

export default PayReceiptView;
